import type { ErrorCode } from '@aw/contracts';
import { ApiError, type ClientErrorCode } from './WorkspaceApi';

type RawError = {
  message?: unknown;
  code?: unknown;
  detail?: unknown;
  error?: { code?: unknown; message?: unknown; detail?: unknown } | string;
};

/**
 * The contract's codes, listed here so the bundle needn't carry zod for one enum. A test checks
 * this list against `ErrorCode.options`.
 */
export const ERROR_CODES = [
  'unauthorized',
  'not_set_up',
  'forbidden',
  'not_found',
  'year_locked',
  'allowance_exceeded',
  'firm_budget_exceeded',
  'not_ready',
  'already_decided',
  'already_answered',
  'reason_required',
  'validation',
  'rate_limited',
  'internal',
] as const satisfies readonly ErrorCode[];

const KNOWN = new Set<string>(ERROR_CODES);

/**
 * Codes an earlier api build sent before it adopted the contract's ErrorBody. Kept so a mixed
 * deployment still shows the right screen; everything maps onto `ErrorCode`.
 */
function legacyCode(code: string, detail: Record<string, unknown> | null): ClientErrorCode | null {
  if (code === 'budget_exceeded') {
    const scope = detail?.scope;
    return scope === 'firm'
      ? 'firm_budget_exceeded'
      : scope === 'run'
        ? 'validation'
        : 'allowance_exceeded';
  }
  if (code === 'deliverable_transition') return 'not_ready';
  if (code === 'fact_immutable') return 'already_decided';
  if (code === 'invalid_request' || code === 'bad_request' || code.startsWith('constraint_'))
    return 'validation';
  return null;
}

/** "Too many wrong tries. Try again in 2 minutes." from the api's retryAfterSeconds, when it says. */
export function waitMessage(detail: Record<string, unknown> | null): string {
  const seconds = typeof detail?.retryAfterSeconds === 'number' ? detail.retryAfterSeconds : null;
  if (seconds === null) return 'Too many tries in a row. Wait a minute, then try again.';
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  return `Too many wrong tries. Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`;
}

function fromStatus(status: number): ClientErrorCode {
  if (status === 401) return 'unauthorized';
  if (status === 429) return 'rate_limited';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status >= 500) return 'internal';
  return 'validation';
}

/** Turns any non-2xx response body into an ApiError carrying a contract ErrorCode. */
export function toApiError(status: number, body: unknown, fallbackMessage: string): ApiError {
  const raw = (body && typeof body === 'object' ? body : {}) as RawError;
  const nested = typeof raw.error === 'object' && raw.error ? raw.error : null;
  const message =
    (typeof raw.message === 'string' && raw.message) ||
    (nested && typeof nested.message === 'string' && nested.message) ||
    (typeof raw.error === 'string' && raw.error) ||
    fallbackMessage;
  const detailRaw = raw.detail ?? nested?.detail;
  const detail =
    detailRaw && typeof detailRaw === 'object' ? (detailRaw as Record<string, unknown>) : null;
  const rawCode =
    typeof raw.code === 'string'
      ? raw.code
      : nested && typeof nested.code === 'string'
        ? nested.code
        : null;
  let code: ClientErrorCode;
  // the sign-in throttle answered 'validation' with reason 'rate_limited' before the contract
  // had its own code; read either as rate_limited
  if (detail?.reason === 'rate_limited') code = 'rate_limited';
  else if (rawCode && KNOWN.has(rawCode)) code = rawCode as ClientErrorCode;
  else code = (rawCode && legacyCode(rawCode, detail)) || fromStatus(status);
  return new ApiError(
    status,
    code === 'rate_limited' ? waitMessage(detail) : message,
    code,
    detail,
  );
}

export const offlineError = () =>
  new ApiError(
    0,
    'Workspace can’t reach the server. Check your connection; it will try again.',
    'offline',
  );

/** What a whole screen says when its data can't load, by code. Errors never apologize. */
export function describeError(e: unknown): { title: string; body: string } {
  const code = e instanceof ApiError ? e.code : null;
  switch (code) {
    case 'offline':
      return {
        title: 'You’re offline',
        body: 'Workspace will load this as soon as the connection is back.',
      };
    case 'not_found':
      return { title: 'Nothing is here', body: 'It may have been removed, or the link is wrong.' };
    case 'forbidden':
      return {
        title: 'This isn’t yours to see',
        body: 'Ask the Workspace manager if you need access.',
      };
    case 'unauthorized':
      return { title: 'Your session ended', body: 'Sign in again to continue.' };
    case 'not_set_up':
      return {
        title: 'Your account isn’t set up for Workspace yet',
        body: 'The Workspace manager adds people and sets their allowance.',
      };
    case 'rate_limited':
      return {
        title: 'Too many tries in a row',
        body: e instanceof ApiError ? e.message : 'Wait a minute, then try again.',
      };
    case 'internal':
      return {
        title: 'Something went wrong on the server',
        body: 'It has been logged. Try again in a moment.',
      };
    default:
      return {
        title: 'This didn’t load',
        body: e instanceof Error && e.message ? e.message : 'Try again in a moment.',
      };
  }
}
